import React, { useCallback, useEffect, useState } from "react";
import globalAxios from "axios";
import {
	FiChevronLeft,
	FiChevronRight,
	FiRefreshCw,
	FiSearch,
	FiUploadCloud
} from "react-icons/fi";
import { client } from "../../Clients";
import { Modal } from "../../Components/Modal";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import GatedButton from "../../Components/GatedButton";
import { DeploymentChangesModal } from "../../Components/Deployment/DeploymentChangesModal";
import AnalysisList from "../Deployment/AnalysisList";
import PreviewRepairSection from "./PreviewRepairSection";
import { useDeploymentAnalysis, usePagesOutOfSync } from "../Deployment/useDeploymentHooks";
import { actionChipClass, actionLabelFr, exactDateFr, relativeTimeFr } from "./publicationUtils";

type Target = "preview" | "production";

const QUEUE_PAGE_SIZE = 50;

type QueueRow = {
	id: string | null;
	contentType: string | null;
	language: string | null;
	slug: string | null;
	contentId: string | null;
	action: string | null;
	status: string | null;
	message: string | null;
	author: string | null;
	error: string | null;
	createdAt?: string | null;
};

type QueuePage = { items: QueueRow[]; total: number; page: number; pageSize: number };

const statusChipClass = (status?: string | null): string => {
	const s = (status ?? "").toLowerCase();
	if (s === "failed") return "border-rose-200 bg-rose-50 text-rose-700";
	if (s === "processing") return "border-amber-200 bg-amber-50 text-amber-700";
	return "border-slate-200 bg-slate-50 text-slate-600";
};

const statusLabelFr = (status?: string | null): string => {
	const s = (status ?? "").toLowerCase();
	if (s === "failed") return "échec";
	if (s === "processing") return "en cours";
	return "en attente";
};

/** Raw deployment queue for one target, paginated server-side (page/pageSize params). */
const RawQueueSection: React.FC<{ target: Target }> = ({ target }) => {
	const [data, setData] = useState<QueuePage>({ items: [], total: 0, page: 1, pageSize: QUEUE_PAGE_SIZE });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(
		async (page: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await globalAxios.get(`/api/deployment/deployment-queue`, {
					withCredentials: true,
					headers: { Accept: "application/json" },
					params: { target, page, pageSize: QUEUE_PAGE_SIZE }
				});
				const d = res.data ?? {};
				setData({
					items: Array.isArray(d.items) ? d.items : [],
					total: Number(d.total ?? 0),
					page: Number(d.page ?? page),
					pageSize: Number(d.pageSize ?? QUEUE_PAGE_SIZE)
				});
			} catch (err) {
				console.error(err);
				setError("Impossible de charger la file de déploiement.");
			} finally {
				setLoading(false);
			}
		},
		[target]
	);

	useEffect(() => {
		load(1);
	}, [load]);

	const totalPages = Math.max(1, Math.ceil(data.total / (data.pageSize || QUEUE_PAGE_SIZE)));

	return (
		<section>
			<div className="mb-2 flex items-center justify-between gap-3">
				<h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">File de déploiement</h4>
				<button
					type="button"
					onClick={() => load(data.page)}
					className="inline-flex items-center gap-1 text-xs text-slate-400 transition hover:text-slate-600"
					title="Rafraîchir"
				>
					<FiRefreshCw className={loading ? "animate-spin" : ""} /> Rafraîchir
				</button>
			</div>
			{error && <p className="mb-2 text-sm text-rose-600">{error}</p>}
			{!error && !loading && data.items.length === 0 && (
				<p className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-700">
					Aucun élément en file pour cette cible.
				</p>
			)}
			{loading && data.items.length === 0 && (
				<div className="flex items-center gap-2 py-3 text-sm text-slate-400">
					<LoadingSpinner size="sm" /> Chargement…
				</div>
			)}
			{data.items.length > 0 && (
				<div className="overflow-hidden rounded-xl border border-slate-200">
					<table className="w-full divide-y divide-slate-100 text-sm">
						<thead className="bg-slate-50">
							<tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
								<th className="px-3 py-2">Contenu</th>
								<th className="px-3 py-2">Action</th>
								<th className="px-3 py-2">Statut</th>
								<th className="px-3 py-2">Auteur</th>
								<th className="px-3 py-2">Date</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100 bg-white">
							{data.items.map((item, i) => (
								<tr key={item.id ?? `${item.contentType}-${item.slug}-${i}`} className="align-top">
									<td className="max-w-[280px] px-3 py-2">
										<p className="truncate font-medium text-slate-800" title={`${item.contentType ?? ""}/${item.slug ?? item.contentId ?? ""}`}>
											{item.contentType ?? "contenu"}
											{item.slug || item.contentId ? `/${item.slug ?? item.contentId}` : ""}
											<span className="ml-1 text-xs font-normal text-slate-400">({item.language ?? "fr"})</span>
										</p>
										{item.message && <p className="truncate text-xs text-slate-500" title={item.message}>{item.message}</p>}
										{item.error && (
											<p className="mt-0.5 truncate text-xs text-rose-600" title={item.error}>
												{item.error}
											</p>
										)}
									</td>
									<td className="px-3 py-2">
										<span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${actionChipClass(item.action)}`}>
											{actionLabelFr(item.action)}
										</span>
									</td>
									<td className="px-3 py-2">
										<span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusChipClass(item.status)}`}>
											{statusLabelFr(item.status)}
										</span>
									</td>
									<td className="px-3 py-2 text-xs text-slate-500">{item.author ?? "-"}</td>
									<td className="px-3 py-2 text-xs text-slate-500" title={exactDateFr(item.createdAt)}>
										{relativeTimeFr(item.createdAt) || "-"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
						<span>
							Page {data.page} sur {totalPages} · {data.total} élément{data.total > 1 ? "s" : ""}
						</span>
						<div className="flex items-center gap-1">
							<button
								type="button"
								disabled={loading || data.page <= 1}
								onClick={() => load(data.page - 1)}
								className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
								title="Page précédente"
							>
								<FiChevronLeft />
							</button>
							<button
								type="button"
								disabled={loading || data.page >= totalPages}
								onClick={() => load(data.page + 1)}
								className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
								title="Page suivante"
							>
								<FiChevronRight />
							</button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

/** Link analysis + out-of-sync report for one target, reusing the existing power tools. */
const AnalysisSection: React.FC<{ target: Target }> = ({ target }) => {
	const { items: outOfSyncItems, load: loadOutOfSync } = usePagesOutOfSync(target);
	const { pageAnalyses, analyzing, analyzed, analyze } = useDeploymentAnalysis(target);
	// « Voir les changements » ouvre la comparaison serveur brouillon/déployé (le même widget
	// que la page Publication et l'audit) ; elle exige l'id admin du contenu.
	const [diffTarget, setDiffTarget] = useState<{ contentId: string; title?: string | null } | null>(null);
	const hasErrors = pageAnalyses.some((pa) => pa.issues.some((i) => i.severity === "error"));

	useEffect(() => {
		loadOutOfSync();
	}, [loadOutOfSync]);

	const analysesToShow = pageAnalyses.filter(
		(pa) => pa.issues.length > 0 || outOfSyncItems.some((i) => i.slug === pa.slug && i.language === pa.language)
	);

	return (
		<section>
			<div className="mb-2 flex items-center justify-between gap-3">
				<h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Analyse des pages</h4>
				<button
					type="button"
					onClick={() => analyze()}
					disabled={analyzing}
					className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{analyzing ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-3.5 w-3.5" />}
					Analyser les liens
				</button>
			</div>
			{outOfSyncItems.length > 0 && (
				<p className="mb-2 text-xs text-slate-500">
					{outOfSyncItems.length} page{outOfSyncItems.length > 1 ? "s" : ""} désynchronisée{outOfSyncItems.length > 1 ? "s" : ""}.
				</p>
			)}
			<AnalysisList
				pageAnalyses={analysesToShow}
				analyzing={analyzing}
				analyzed={analyzed}
				hasErrors={hasErrors}
				targetLabel={target === "production" ? "Production" : "Preview"}
				pendingItems={outOfSyncItems}
				onViewChanges={(pa) => {
					if (pa.id) setDiffTarget({ contentId: pa.id, title: pa.title ?? pa.slug });
				}}
				onDeploySingle={undefined}
				deployingSlugLanguage={null}
				disableDeploySingle={true}
			/>
			{diffTarget && (
				<DeploymentChangesModal
					show
					onClose={() => setDiffTarget(null)}
					contentId={diffTarget.contentId}
					target={target}
					title={diffTarget.title}
				/>
			)}
		</section>
	);
};

/**
 * Maintenance actions: manual preview resync (the ONLY place a preview deploy can still be
 * triggered by hand) and the legacy promote path. The hard-clean republish lives in
 * Settings → Déploiement (its canonical home), so it is not duplicated here.
 */
const MaintenanceSection: React.FC = () => {
	const [resyncing, setResyncing] = useState(false);
	const [promoting, setPromoting] = useState(false);
	const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
	const [feedback, setFeedback] = useState<{ tone: "success" | "error"; text: string } | null>(null);

	const resyncPreview = async () => {
		setResyncing(true);
		setFeedback(null);
		try {
			await client.deployment.apiDeploymentDeploymentQueueDeployPost("preview", {
				withCredentials: true,
				headers: { Accept: "application/json" }
			});
			setFeedback({ tone: "success", text: "Preview resynchronisée." });
		} catch (err: any) {
			console.error(err);
			setFeedback({
				tone: "error",
				text: err?.response?.status === 409 ? "Une synchronisation est déjà en cours." : "Échec de la resynchronisation."
			});
		} finally {
			setResyncing(false);
		}
	};

	const promote = async () => {
		setPromoting(true);
		setFeedback(null);
		try {
			const res = await globalAxios.post<{ total?: number; succeeded?: number; failed?: number }>(
				`/api/deployment/promote`,
				null,
				{ withCredentials: true, params: { target: "production" } }
			);
			const d = res.data ?? {};
			setFeedback({
				tone: (d.failed ?? 0) > 0 ? "error" : "success",
				text:
					(d.failed ?? 0) > 0
						? `Promotion : ${d.succeeded ?? 0}/${d.total ?? 0} réussie(s), ${d.failed} échec(s).`
						: `Promotion terminée (${d.succeeded ?? 0} élément${(d.succeeded ?? 0) > 1 ? "s" : ""}).`
			});
		} catch (err) {
			console.error(err);
			setFeedback({ tone: "error", text: "La promotion a échoué. Consultez les journaux." });
		} finally {
			setPromoting(false);
		}
	};

	const cardClass = "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
	const actionBtnClass =
		"inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

	return (
		<section>
			<h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Maintenance</h4>
			{feedback && (
				<p
					className={`mb-3 rounded-lg border px-3 py-2 text-sm ${
						feedback.tone === "success"
							? "border-emerald-200 bg-emerald-50 text-emerald-700"
							: "border-rose-200 bg-rose-50 text-rose-700"
					}`}
				>
					{feedback.text}
				</p>
			)}
			<div className="grid gap-3 sm:grid-cols-2">
				<div className={cardClass}>
					<p className="text-sm font-semibold text-slate-800">Resynchroniser la preview</p>
					<p className="mt-1 text-xs text-slate-500">
						La preview se publie automatiquement après chaque enregistrement. Ce déclenchement manuel
						ne sert qu'en cas de retard inhabituel.
					</p>
					<GatedButton token="feature:deployment:write" onClick={resyncPreview} disabled={resyncing} className={`mt-3 ${actionBtnClass}`}>
						{resyncing ? <LoadingSpinner size="sm" inline /> : <FiRefreshCw className="h-3.5 w-3.5" />}
						Resynchroniser
					</GatedButton>
				</div>
				<div className={cardClass}>
					<p className="text-sm font-semibold text-slate-800">Promotion héritée</p>
					<p className="mt-1 text-xs text-slate-500">
						Ancien chemin de promotion globale vers la production. Préférez « Publier tout en production ».
					</p>
					<GatedButton token="feature:deployment:write" onClick={() => setShowPromoteConfirm(true)} disabled={promoting} className={`mt-3 ${actionBtnClass}`}>
						{promoting ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-3.5 w-3.5" />}
						Promouvoir vers la production
					</GatedButton>
				</div>
			</div>

			{/* Legacy promote confirmation */}
			<Modal show={showPromoteConfirm} onHide={() => setShowPromoteConfirm(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Promouvoir vers la production ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Tout ce qui est prêt en preview sera poussé vers la production, immédiatement visible par les
						visiteurs du site.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowPromoteConfirm(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={() => {
							setShowPromoteConfirm(false);
							promote();
						}}
						className="inline-flex items-center rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a]"
					>
						Confirmer la promotion
					</button>
				</Modal.Footer>
			</Modal>
		</section>
	);
};

/**
 * « Outils de déploiement » : the deployment power tools, moved off the deployment page into
 * Settings → Déploiement: a raw paginated queue per target, link analysis, preview repair, and
 * maintenance actions (preview resync, legacy promote). A preview/production selector (production
 * by default) scopes the target-specific sections. Expert territory; day-to-day publishing lives
 * on the deployment page.
 */
export const AdvancedDeploymentTools: React.FC = () => {
	const [target, setTarget] = useState<Target>("production");

	return (
		<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
			<div className="mb-5 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1">
				{(["preview", "production"] as const).map((t) => (
					<button
						key={t}
						type="button"
						onClick={() => setTarget(t)}
						className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
							target === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
						}`}
					>
						{t === "preview" ? "Preview" : "Production"}
					</button>
				))}
			</div>
			<div className="space-y-6">
				<RawQueueSection key={`queue-${target}`} target={target} />
				<AnalysisSection key={`analysis-${target}`} target={target} />
				<PreviewRepairSection />
				<MaintenanceSection />
			</div>
		</section>
	);
};

export default AdvancedDeploymentTools;
